import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { describeApiError } from '@/lib/describeApiError';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  CircularProgress,
  Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { format } from 'date-fns';
import { useNotify } from '@/hooks/useNotify';
import { PriorityChip } from '@/components/tasks/PriorityChip';
import {
  useTask,
  useStartTask,
  usePauseTask,
  useResumeTask,
  useCompleteTask,
  useClaimTask,
} from '@/hooks/useTasks';
import { dateFnsLocale } from '@/utils/locale';

const taskStatusChipColors: Record<string, 'default' | 'primary' | 'warning' | 'success' | 'error'> = {
  pending: 'default',
  confirmed: 'primary',
  in_progress: 'primary',
  paused: 'warning',
  completed: 'success',
  cancelled: 'error',
};

export default function TaskDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const notify = useNotify();

  const { data: task, isLoading, error } = useTask(id!);
  const startTask = useStartTask();
  const pauseTask = usePauseTask();
  const resumeTask = useResumeTask();
  const completeTask = useCompleteTask();
  const claimTask = useClaimTask();

  const handleTaskAction = useCallback(async (action: string, taskId: string) => {
    try {
      switch (action) {
        case 'start':
          await startTask.mutateAsync(taskId);
          notify.success(t('myTasks.started'));
          break;
        case 'pause':
          await pauseTask.mutateAsync(taskId);
          notify.success(t('myTasks.paused'));
          break;
        case 'resume':
          await resumeTask.mutateAsync(taskId);
          notify.success(t('myTasks.resumed'));
          break;
        case 'complete':
          await completeTask.mutateAsync(taskId);
          notify.success(t('myTasks.completed'));
          break;
        case 'claim':
          await claimTask.mutateAsync(taskId);
          notify.success(t('myTasks.claimed'));
          break;
      }
    } catch (error) {
      notify.error(describeApiError(error, tc, { fallback: t('myTasks.actionFailed') }));
    }
  }, [startTask, pauseTask, resumeTask, completeTask, claimTask, notify, t, tc]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !task) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {error ? t('myTasks.loadError') : t('myTasks.taskNotFound')}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/tasks')}
          sx={{ mt: 2 }}
        >
          {t('common.back')}
        </Button>
      </Box>
    );
  }

  const isDeadlinePassed = task.deadline && new Date(task.deadline) < new Date();

  return (
    <Box sx={{ p: 3, maxWidth: 800 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Tooltip title={t('myTasks.back')}>
          <IconButton onClick={() => navigate('/tasks')} aria-label={t('myTasks.back')}>
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <TaskAltIcon color="action" />
        <Typography variant="h5" component="h1" sx={{ flex: 1 }}>
          {task.title}
        </Typography>
      </Stack>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack spacing={3}>
            {/* Task Description */}
            {task.description && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  {t('myTasks.description')}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {task.description}
                </Typography>
              </Box>
            )}

            {/* Task Properties */}
            <Box>
              <Typography variant="h6" gutterBottom>
                {t('myTasks.details')}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <PriorityChip priority={task.priority} size="medium" />
                <Chip
                  label={t(`status.${task.status}`, task.status)}
                  color={taskStatusChipColors[task.status] ?? 'default'}
                />
                {task.assignedTo ? (
                  <Chip
                    label={`${task.assignedTo.firstName} ${task.assignedTo.lastName}`}
                    variant="outlined"
                  />
                ) : (
                  <Chip label={t('myTasks.unassigned')} variant="outlined" color="warning" />
                )}
                {task.scheduledAt && (
                  <Chip
                    icon={<AccessTimeIcon />}
                    label={format(new Date(task.scheduledAt), 'PPPp', { locale: dateFnsLocale() })}
                    variant="outlined"
                  />
                )}
                {task.estimatedDurationHours != null && (
                  <Chip
                    label={t('myTasks.durationHours', { hours: task.estimatedDurationHours })}
                    variant="outlined"
                  />
                )}
                {task.deadline && (
                  <Chip
                    label={isDeadlinePassed ? t('myTasks.deadlinePassed') : t('myTasks.deadline')}
                    color={isDeadlinePassed ? 'error' : 'default'}
                    variant="outlined"
                  />
                )}
              </Stack>
            </Box>

            {/* Associated Work Order */}
            {task.booking && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  {t('myTasks.associatedWorkOrder')}
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<OpenInNewIcon />}
                  onClick={() => navigate(`/workorders/${task.booking!.id}`)}
                >
                  {task.booking.vehicleRegNumber} — {task.booking.serviceType}
                </Button>
              </Box>
            )}

            {/* Created By */}
            <Box>
              <Typography variant="body2" color="text.secondary">
                {t('myTasks.createdBy')}: {task.createdBy.firstName} {task.createdBy.lastName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('myTasks.createdAt')}: {format(new Date(task.createdAt), 'PPPp', { locale: dateFnsLocale() })}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('myTasks.actions')}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {!task.assignedTo && (
              <Button
                variant="outlined"
                startIcon={<PersonAddIcon />}
                onClick={() => handleTaskAction('claim', task.id)}
              >
                {t('myTasks.claim')}
              </Button>
            )}
            {task.status === 'pending' && task.assignedTo && (
              task.booking?.status && ['quote', 'pending', 'confirmed', 'quote_rejected'].includes(task.booking.status) ? (
                <Tooltip title={t('myTasks.vehicleNotArrived')}>
                  <span>
                    <Button
                      variant="outlined"
                      disabled
                      startIcon={<PlayArrowIcon />}
                    >
                      {t('myTasks.start')}
                    </Button>
                  </span>
                </Tooltip>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<PlayArrowIcon />}
                  onClick={() => handleTaskAction('start', task.id)}
                >
                  {t('myTasks.start')}
                </Button>
              )
            )}
            {task.status === 'in_progress' && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<PauseIcon />}
                  onClick={() => handleTaskAction('pause', task.id)}
                >
                  {t('myTasks.pause')}
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircleIcon />}
                  onClick={() => handleTaskAction('complete', task.id)}
                >
                  {t('myTasks.complete')}
                </Button>
              </>
            )}
            {task.status === 'paused' && (
              <>
                {/*
                  Outlined, not contained (#2726). Resume and Complete were
                  both contained here, so neither read as THE action — while
                  the in_progress branch above already gets this right with
                  an outlined Pause beside a contained Complete.
                */}
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<PlayArrowIcon />}
                  onClick={() => handleTaskAction('resume', task.id)}
                >
                  {t('myTasks.resume')}
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircleIcon />}
                  onClick={() => handleTaskAction('complete', task.id)}
                >
                  {t('myTasks.complete')}
                </Button>
              </>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}